import {useCallback, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';

interface IUseLeavePageProps {
  leaveUrl?: string;
  canTrigger?: boolean;
}

const useLeavePage = ({leaveUrl, canTrigger = true}: IUseLeavePageProps) => {
  const navigate = useNavigate();

  const showPrompt = useCallback(() => {
    return window.confirm('Changes that you made are not saved. Leave?');
  }, []);

  const handleLeave = useCallback(() => {
    leaveUrl ? navigate(leaveUrl) : history.back();
  }, [leaveUrl]);

  const handleManualLeave = useCallback(() => {
    if (!canTrigger || showPrompt()) {
      handleLeave();
    }
  }, [canTrigger, showPrompt, handleLeave]);

  useEffect(() => {
    if (!canTrigger) {
      return;
    }
    const controller = new AbortController();
    window.addEventListener(
      'beforeunload',
      e => {
        e.preventDefault();
        e.returnValue = '';
        return '';
      },
      {signal: controller.signal},
    );
    return () => {
      controller.abort();
    };
  }, [canTrigger]);

  const pushInitRef = useRef(true);
  useEffect(() => {
    if (!canTrigger) {
      if (!pushInitRef.current) {
        history.go(-1);
        pushInitRef.current = true;
      }
      return;
    }
    if (pushInitRef.current) {
      history.pushState({guard: true}, '', window.location.href);
      pushInitRef.current = false;
    }
    let promptable = true;
    const controller = new AbortController();
    window.addEventListener(
      'popstate',
      _e => {
        if (!promptable) {
          return;
        }
        if (showPrompt()) {
          handleLeave();
        } else {
          promptable = false;
          setTimeout(() => (promptable = true), 10);
          history.go(1);
        }
      },
      {signal: controller.signal},
    );
    return () => {
      controller.abort();
    };
  }, [canTrigger, showPrompt, handleLeave]);

  return {handleManualLeave};
};

export default useLeavePage;
